/**
 * MarketingSectionRewardsByRank.tsx - Rank-Based Rewards Configuration
 *
 * This component manages competitive rank-based rewards for promoters with
 * complex validation logic for rank ranges and minimum donation requirements.
 *
 * Key Features:
 * - Rank-based reward tier configuration with ranges
 * - Minimum donation requirements with cascading logic
 * - Rank range validation and contiguity checking
 * - Add, edit, and delete rank tiers with validation
 * - Real-time validation with helpful error messages
 * - Preview of rank structure and requirements
 *
 * Rank Configuration:
 * - Rank Range: Single rank (e.g., "1") or range (e.g., "2-5")
 * - Reward Description: What promoters receive at this rank
 * - Minimum Donation: Required amount to qualify for rank
 * - Cascading logic for unqualified promoters
 *
 * Validation Rules:
 * - Contiguous rank sequences (no gaps)
 * - Descending minimum requirements (higher ranks = higher minimums)
 * - Valid rank format (single numbers or ranges)
 * - No overlapping rank ranges
 *
 * Cascading Logic:
 * - Promoters who don't meet minimums get bumped to lower tiers
 * - Automatic rank reassignment based on actual performance
 * - Fair distribution of rewards based on achievement
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, rank validation utilities, cascading logic
 */

import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Plus, Check, X, Pencil, Trash2 } from "lucide-react";
import {
  validateRankInput,
  validateRankMinimum,
  getRankPlaceholder,
  getNextAvailableRank,
  formatCurrencyForValidation,
} from "../../../utils/rankValidation";
import { CreateFundraiserModel } from "../../../models";
import { RankAchievementModel } from "../../../models/FundraisersGridModels";

const LevelsCaption = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
`;

const AddLinksContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
`;

const AddLinkButton = styled.button<{ disabled?: boolean }>`
  background: none;
  border: none;
  color: ${(props) => (props.disabled ? "#9ca3af" : "#3b82f6")};
  fontsize: 16px;
  fontweight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  padding: 0;
  textdecoration: none;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;

const RanksTableHeader = styled.div`
  display: grid;
  grid-template-columns: 140px 330px 160px 80px;
  padding: 12px 16px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  marginbottom: 0px;
  width: 750px;
  fontsize: 12px;
  fontweight: 700;
  color: #374151;
  texttransform: uppercase;
  letterspacing: 0.5px;
  align-items: center;
`;

const RankRowContainer = styled.div<{ hasError?: boolean }>`
  display: grid;
  grid-template-columns: 140px 330px 160px 80px;
  align-items: flex-start;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  marginbottom: 0px;
  width: 750px;
  min-height: ${(props) => (props.hasError ? "88px" : "56px")};
`;

const IconColumn = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  align-items: center;
  padding: 0;
`;

const InputCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const CurrencyInput = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const CurrencySymbol = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  z-index: 1;
`;

const CurrencyField = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px 10px 32px;
  height: 36px;
  border: 1px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  background-color: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const IconButton = styled.button<{ variant: "save" | "cancel" }>`
  transition: all 0.2s ease;
  padding: 8px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  ${(props) =>
    props.variant === "save"
      ? `
    background: #10b981;
    color: #ffffff;
    
    &:hover {
      background: #059669;
    }
    
    &:disabled {
      background: #dcfce7;
      color: #059669;
      cursor: not-allowed;
      opacity: 1;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;
    
    &:hover {
      background: #e2e8f0;
    }
  `}
`;

const EditDeleteButton = styled.button<{
  variant: "edit" | "delete";
  disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  padding: 8px;
  opacity: 1;

  ${(props) =>
    props.variant === "edit"
      ? `
    background: #f1f5f9;
    color: #64748b;
    
    &:hover {
      background: #e2e8f0;
    }
  `
      : `
    background: #fee2e2;
    color: #dc2626;
    
    &:hover {
      background: #fecaca;
    }
  `}
`;

interface MarketingSectionRewardsByRankProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
  onSave?: () => void;
}

interface RankRowProps {
  rank: RankAchievementModel;
  existingRanks: string[];
  borderRadius: string;
  builderData: any;
  isGlobalEditing: boolean;
  onUpdate: (rank: RankAchievementModel) => void;
  onCancel: () => void;
  onDelete: () => void;
  hideEditDelete?: boolean;
  onSave?: () => void;
  editPosition?: number;
}

// Helper function to parse rank string and get start/end values
const parseRankString = (
  rankStr: string
): { start: number; end: number } | null => {
  if (!rankStr || rankStr.trim() === "") return null;

  const rangeMatch = rankStr.match(/^(\d+)-(\d+)$/);
  const singleMatch = rankStr.match(/^(\d+)$/);

  if (singleMatch) {
    const rank = parseInt(singleMatch[1]);
    return { start: rank, end: rank };
  } else if (rangeMatch) {
    const start = parseInt(rangeMatch[1]);
    const end = parseInt(rangeMatch[2]);
    return { start, end };
  }

  return null;
};

// Helper function to adjust ranks to maintain continuity
const adjustRanksForContinuity = (
  ranks: RankAchievementModel[],
  editedIndex: number,
  newRankStr: string
) => {
  const newParsed = parseRankString(newRankStr);
  if (!newParsed) return ranks;

  const adjustedRanks = [...ranks];

  // Update the edited rank
  adjustedRanks[editedIndex] = {
    ...adjustedRanks[editedIndex],
    rank: newRankStr,
  };

  // Adjust all ranks after the edited one
  let nextStartRank = newParsed.end + 1;

  for (let i = editedIndex + 1; i < adjustedRanks.length; i++) {
    const currentRankParsed = parseRankString(adjustedRanks[i].rank);
    if (currentRankParsed) {
      // Calculate the number of spots in this rank
      const spotCount = currentRankParsed.end - currentRankParsed.start + 1;

      // Create new rank string maintaining the same number of spots
      if (spotCount === 1) {
        adjustedRanks[i] = {
          ...adjustedRanks[i],
          rank: nextStartRank.toString(),
        };
      } else {
        adjustedRanks[i] = {
          ...adjustedRanks[i],
          rank: `${nextStartRank}-${nextStartRank + spotCount - 1}`,
        };
      }

      nextStartRank += spotCount;
    }
  }

  return adjustedRanks;
};

const RankRow: React.FC<RankRowProps> = ({
  rank,
  existingRanks,
  borderRadius,
  builderData,
  isGlobalEditing,
  onUpdate,
  onCancel,
  onDelete,
  hideEditDelete = false,
  onSave,
  editPosition,
}) => {
  const [isEditing, setIsEditing] = useState(rank.isEditing || false);
  const [tempRank, setTempRank] = useState(rank.rank?.toString() || "");
  const [tempReward, setTempReward] = useState(rank.reward || "");
  const [tempMinimum, setTempMinimum] = useState(
    rank.minimum?.toString() || "1"
  );
  const [rankError, setRankError] = useState(false);
  const [rewardError, setRewardError] = useState(false);
  const [rewardDuplicateError, setRewardDuplicateError] = useState(false);
  const [minimumErrorMessage, setMinimumErrorMessage] = useState("");
  const [rewardErrorMessage, setRewardErrorMessage] = useState("");

  // Calculate the dynamic placeholder based on previous rows
  const getDynamicRankPlaceholder = () => {
    if (editPosition === 0) {
      return "1, 1-3...";
    }

    // Get the previous row (which should be the row directly above)
    const sortedRanks = (builderData.ranks || [])
      .slice()
      .sort((a: any, b: any) => {
        const aRank =
          typeof a.rank === "string" ? parseInt(a.rank.split("-")[0]) : a.rank;
        const bRank =
          typeof b.rank === "string" ? parseInt(b.rank.split("-")[0]) : b.rank;
        return (aRank || 0) - (bRank || 0);
      });

    const previousRow = sortedRanks[(editPosition ?? 0) - 1];
    if (!previousRow) {
      return "1, 1-3...";
    }

    const prevRankStr = previousRow.rank?.toString() || "";
    const rangeMatch = prevRankStr.match(/^(\d+)-(\d+)$/);
    const singleMatch = prevRankStr.match(/^(\d+)$/);

    let highestRankInPreviousRow = 1;
    if (singleMatch) {
      highestRankInPreviousRow = parseInt(singleMatch[1]);
    } else if (rangeMatch) {
      highestRankInPreviousRow = parseInt(rangeMatch[2]);
    }

    const nextStartRank = highestRankInPreviousRow + 1;
    const nextEndRank = nextStartRank + 10;
    return `${nextStartRank}, ${nextStartRank}-${nextEndRank}...`;
  };

  // Check if reward name is already used by other ranks
  const isDuplicateRewardName = () => {
    if (!tempReward.trim()) return false;

    const allRanks = builderData.ranks || [];
    const currentIndex = editPosition || 0;

    // Check all other ranks for duplicate reward names
    for (let i = 0; i < allRanks.length; i++) {
      if (i !== currentIndex) {
        const otherReward = allRanks[i]?.reward?.trim() || "";
        if (otherReward.toLowerCase() === tempReward.trim().toLowerCase()) {
          return true;
        }
      }
    }

    return false;
  };

  // Strict rank continuity validation
  const validateRankContinuity = () => {
    if (!tempRank.trim()) return false; // Empty rank is invalid

    if (editPosition === 0) {
      // First row must start with 1
      const rangeMatch = tempRank.match(/^(\d+)-(\d+)$/);
      const singleMatch = tempRank.match(/^(\d+)$/);

      if (singleMatch) {
        return parseInt(singleMatch[1]) === 1;
      } else if (rangeMatch) {
        return parseInt(rangeMatch[1]) === 1;
      }
      return false;
    } else {
      // Subsequent rows must start exactly after previous row ends
      const sortedRanks = (builderData.ranks || [])
        .slice()
        .sort((a: any, b: any) => {
          const aRank =
            typeof a.rank === "string"
              ? parseInt(a.rank.split("-")[0])
              : a.rank;
          const bRank =
            typeof b.rank === "string"
              ? parseInt(b.rank.split("-")[0])
              : b.rank;
          return (aRank || 0) - (bRank || 0);
        });

      const previousRow = sortedRanks[(editPosition ?? 0) - 1];
      if (!previousRow) return false;

      const prevRankStr = previousRow.rank?.toString() || "";
      const prevRangeMatch = prevRankStr.match(/^(\d+)-(\d+)$/);
      const prevSingleMatch = prevRankStr.match(/^(\d+)$/);

      let expectedStartRank = 1;
      if (prevSingleMatch) {
        expectedStartRank = parseInt(prevSingleMatch[1]) + 1;
      } else if (prevRangeMatch) {
        expectedStartRank = parseInt(prevRangeMatch[2]) + 1;
      }

      const rangeMatch = tempRank.match(/^(\d+)-(\d+)$/);
      const singleMatch = tempRank.match(/^(\d+)$/);

      if (singleMatch) {
        return parseInt(singleMatch[1]) === expectedStartRank;
      } else if (rangeMatch) {
        return parseInt(rangeMatch[1]) === expectedStartRank;
      }
      return false;
    }
  };

  // Helper function that returns validation results without updating state
  const performValidation = () => {
    const hasEmptyFields = {
      rank: tempRank.trim() === "",
      reward: tempReward.trim() === "",
      minimum: false, // Remove minimum validation
    };

    const hasValidRankContinuity = validateRankContinuity();
    const hasDuplicateReward = isDuplicateRewardName();

    return {
      ...hasEmptyFields,
      rankContinuity: !hasValidRankContinuity,
      duplicateReward: hasDuplicateReward,
    };
  };

  // Function to validate current row's minimum against other rows
  const validateCurrentMinimum = () => {
    const currentMinimum = Number(tempMinimum) || 1;
    const allRanks = builderData.ranks || [];
    const currentIndex = editPosition || 0;

    // Check only the immediately previous row (higher rank)
    if (currentIndex > 0) {
      const previousMinimum = Number(allRanks[currentIndex - 1]?.minimum) || 1;
      if (previousMinimum < currentMinimum) {
        return `Cannot be greater than $${previousMinimum.toLocaleString()}`;
      }
    }

    // Check only the immediately next row (lower rank)
    if (currentIndex < allRanks.length - 1) {
      const nextMinimum = Number(allRanks[currentIndex + 1]?.minimum) || 1;
      if (nextMinimum > currentMinimum) {
        return `Cannot be less than $${nextMinimum.toLocaleString()}`;
      }
    }

    return ""; // No error
  };

  // Function to validate current row's reward name against other rows
  const validateCurrentReward = () => {
    if (!tempReward.trim()) return ""; // Empty is handled by basic validation

    const allRanks = builderData.ranks || [];
    const currentIndex = editPosition || 0;

    // Check all other ranks for duplicate reward names
    for (let i = 0; i < allRanks.length; i++) {
      if (i !== currentIndex) {
        const otherReward = allRanks[i]?.reward?.trim() || "";
        if (otherReward.toLowerCase() === tempReward.trim().toLowerCase()) {
          return "Reward name must be unique";
        }
      }
    }

    return ""; // No error
  };

  // Function to check if save is possible (used for save button disabled state)
  const canSave = () => {
    const validation = performValidation();
    // Always allow saving - don't disable save button for validation errors
    return tempRank.trim() !== "" && tempReward.trim() !== "";
  };

  const handleSave = () => {
    const validation = performValidation();
    const minimumValidation = validateCurrentMinimum();
    const rewardValidation = validateCurrentReward();

    // Update error states (make sure UI shows red borders & messages)
    setRankError(validation.rank || validation.rankContinuity);
    setRewardError(validation.reward);
    setRewardDuplicateError(validation.duplicateReward);
    setMinimumErrorMessage(minimumValidation);
    setRewardErrorMessage(rewardValidation);

    // Prevent save if invalid
    if (
      validation.rank ||
      validation.reward ||
      validation.rankContinuity ||
      validation.duplicateReward ||
      rewardValidation
    ) {
      // Keep user in edit mode to fix input
      setIsEditing(true);
      return;
    }

    if (minimumValidation) {
      setIsEditing(true);
      return;
    }

    // Save valid row
    const cleanMinimum = tempMinimum.replace(/[^0-9]/g, "");
    const newMinimum = Math.max(1, Number(cleanMinimum) || 1);

    const updatedRank = new RankAchievementModel({
      ...rank,
      rank: tempRank,
      reward: tempReward,
      minimum: newMinimum,
      isEditing: false,
    });

    setIsEditing(false);
    setRankError(false);
    setRewardError(false);
    setRewardDuplicateError(false);
    setMinimumErrorMessage("");
    setRewardErrorMessage("");

    onUpdate(updatedRank);
    if (onSave) onSave();
  };

  const handleCancel = () => {
    setTempRank(rank.rank?.toString() || "");
    setTempReward(rank.reward || "");
    setTempMinimum(rank.minimum?.toString() || "1");
    setIsEditing(false);
    setRankError(false);
    setRewardError(false);
    setRewardDuplicateError(false);
    setMinimumErrorMessage("");
    setRewardErrorMessage("");
    // Only delete new ranks that haven't been saved yet
    if (rank.isNew && rank.isEditing) {
      onCancel();
    }
  };

  const handleEdit = () => {
    setTempRank(rank.rank?.toString() || "");
    setTempReward(rank.reward || "");
    setTempMinimum(rank.minimum?.toString() || "1");
    setIsEditing(true);
  };

  const handleRankBlur = () => {
    const validation = performValidation();
    setRankError(validation.rank || validation.rankContinuity);
  };

  const handleRewardBlur = () => {
    setRewardError(tempReward.trim() === "");
    setRewardDuplicateError(isDuplicateRewardName());
  };

  const handleMinimumChange = (value: string) => {
    const cleanValue = value.replace(/[^0-9]/g, "");
    setTempMinimum(cleanValue);
    // Clear error when user starts typing
    if (minimumErrorMessage) {
      setMinimumErrorMessage("");
    }
  };

  if (isEditing) {
    return (
      <RankRowContainer
        style={{ borderRadius: borderRadius }}
        hasError={!!minimumErrorMessage || !!rewardErrorMessage}
      >
        <InputCellWrapper>
          <input
            type="text"
            value={tempRank}
            onChange={(e) => setTempRank(e.target.value)}
            onBlur={handleRankBlur}
            style={{
              width: "85px",
              padding: "10px 12px",
              height: "36px",
              boxSizing: "border-box",
              border: `1px solid ${rankError ? "#dc2626" : "#e2e8f0"}`,
              backgroundColor: rankError ? "#fef2f2" : "#ffffff",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            placeholder={getDynamicRankPlaceholder()}
          />
          {rankError && (
            <div
              style={{
                fontSize: "12px",
                color: "#dc2626",
                fontWeight: "500",
                marginTop: "4px",
                textAlign: "left",
                whiteSpace: "nowrap",
                top: "38px",
                left: "0",
              }}
            >
              {tempRank.trim() === ""
                ? "Rank is required"
                : "Rank must continue from the previous rank"}
            </div>
          )}
        </InputCellWrapper>
        <InputCellWrapper>
          <input
            type="text"
            value={tempReward}
            onChange={(e) => setTempReward(e.target.value)}
            onBlur={handleRewardBlur}
            maxLength={40}
            style={{
              width: "305px",
              padding: "10px 12px",
              height: "36px",
              boxSizing: "border-box",
              border: `1px solid ${rewardError || rewardDuplicateError || rewardErrorMessage ? "#dc2626" : "#e2e8f0"}`,
              backgroundColor:
                rewardError || rewardDuplicateError || rewardErrorMessage
                  ? "#fef2f2"
                  : "#ffffff",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            placeholder="Enter reward description..."
          />
          {(rewardDuplicateError || rewardErrorMessage) && (
            <div
              style={{
                fontSize: "12px",
                color: "#dc2626",
                fontWeight: "500",
                marginTop: "4px",
                textAlign: "left",
                whiteSpace: "nowrap",
                position: "absolute",
                top: "38px",
                left: "0",
              }}
            >
              {rewardErrorMessage || "Reward name must be unique"}
            </div>
          )}
        </InputCellWrapper>
        <InputCellWrapper>
          <CurrencyInput>
            <CurrencySymbol>$</CurrencySymbol>
            <CurrencyField
              type="number"
              placeholder="1 minimum"
              value={tempMinimum}
              onChange={(e) => {
                handleMinimumChange(e.target.value);
              }}
              hasError={!!minimumErrorMessage}
              style={{ width: "140px" }}
              min="1"
            />
          </CurrencyInput>
          {minimumErrorMessage && (
            <div
              style={{
                fontSize: "12px",
                color: "#dc2626",
                fontWeight: "500",
                marginTop: "4px",
                textAlign: "left",
                whiteSpace: "nowrap",
                position: "absolute",
                top: "38px",
                left: "0",
              }}
            >
              {minimumErrorMessage}
            </div>
          )}
        </InputCellWrapper>
        <IconColumn>
          <IconButton variant="save" type="button" onClick={handleSave}>
            <Check size={18} />
          </IconButton>
          <IconButton variant="cancel" type="button" onClick={handleCancel}>
            <X size={18} />
          </IconButton>
        </IconColumn>
      </RankRowContainer>
    );
  }

  return (
    <RankRowContainer style={{ borderRadius: borderRadius }}>
      <div>
        <span style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>
          {rank.rank || "0"}
        </span>
      </div>
      <div style={{ fontSize: "14px", color: "#1e293b" }}>{rank.reward}</div>
      <div style={{ fontSize: "14px", color: "#1e293b" }}>
        {rank.minimum && rank.minimum > 0
          ? formatCurrencyForValidation(rank.minimum)
          : "—"}
      </div>
      <IconColumn style={{ display: hideEditDelete ? "none" : "flex" }}>
        <EditDeleteButton variant="edit" type="button" onClick={handleEdit}>
          <Pencil size={16} />
        </EditDeleteButton>
        <EditDeleteButton variant="delete" type="button" onClick={onDelete}>
          <Trash2 size={16} />
        </EditDeleteButton>
      </IconColumn>
    </RankRowContainer>
  );
};

export const MarketingSectionRewardsByRank: React.FC<MarketingSectionRewardsByRankProps> =
  observer(({ builderData, updateBuilderField, onSave }) => {
    // Sort ranks by rank ascending on render
    const sortedRanks = (builderData.ranks || [])
      .slice()
      .sort((a: RankAchievementModel, b: RankAchievementModel) => {
        const aRank =
          typeof a.rank === "string" ? parseInt(a.rank.split("-")[0]) : a.rank;
        const bRank =
          typeof b.rank === "string" ? parseInt(b.rank.split("-")[0]) : b.rank;
        return (aRank || 0) - (bRank || 0);
      });

    // Calculate if any row is currently being edited
    const isGlobalEditing = React.useMemo(() => {
      return (builderData.ranks || []).some((r: any) => r.isEditing === true);
    }, [builderData.ranks]);

    // Get next available rank using utility
    const getNextRank = (): string => {
      const currentRanks = (builderData.ranks || [])
        .map((r) => r.rank?.toString())
        .filter(Boolean);
      return getNextAvailableRank(currentRanks);
    };

    return (
      <>
        {/* Ranks Section */}
        {sortedRanks.length > 0 && (
          <>
            <div style={{ marginTop: "40px" }}>
              <LevelsCaption>By Rank</LevelsCaption>
            </div>

            <RanksTableHeader>
              <div>RANK</div>
              <div>REWARD</div>
              <div>MINIMUM</div>
              <div></div>
            </RanksTableHeader>

            {sortedRanks.map((rank: RankAchievementModel, index: number) => {
              const existingRanks = sortedRanks
                .map((r: RankAchievementModel) => r.rank)
                .filter((rankValue: string) => rankValue !== undefined);
              const isFirstRow = index === 0 && sortedRanks.length === 1;
              const isLastRow =
                index === sortedRanks.length - 1 && sortedRanks.length > 1;
              const isMiddleRow =
                sortedRanks.length > 1 &&
                index !== 0 &&
                index !== sortedRanks.length - 1;
              const isSingleRow = sortedRanks.length === 1;

              let borderRadius;
              if (isSingleRow) {
                borderRadius = "0 0 8px 8px";
              } else if (isLastRow) {
                borderRadius = "0 0 8px 8px";
              } else {
                borderRadius = "0";
              }

              return (
                <RankRow
                  key={rank.id}
                  rank={rank}
                  existingRanks={existingRanks}
                  borderRadius={borderRadius}
                  editPosition={index}
                  builderData={builderData}
                  isGlobalEditing={isGlobalEditing}
                  onUpdate={(updatedRank: RankAchievementModel) => {
                    const currentRanks = builderData.ranks || [];

                    // Step 1: Update the current rank with all fields preserved
                    const ranksWithCurrentUpdate = currentRanks.map(
                      (r: RankAchievementModel, i: number) =>
                        i === index
                          ? new RankAchievementModel({ ...r, ...updatedRank })
                          : r
                    );
                    // Step 2: Apply rank continuity adjustments
                    const ranksWithAdjustedPositions: RankAchievementModel[] =
                      adjustRanksForContinuity(
                        ranksWithCurrentUpdate,
                        index,
                        updatedRank.rank
                      );

                    // Save the final ranks
                    updateBuilderField("ranks", ranksWithAdjustedPositions);
                    if (onSave) {
                      onSave();
                    }
                  }}
                  onCancel={() => {
                    if (rank.isNew) {
                      const updated = sortedRanks.filter(
                        (r: any) => r.id !== rank.id
                      );
                      updateBuilderField("ranks", updated);
                    }
                  }}
                  onDelete={() => {
                    const updated = sortedRanks.filter(
                      (r: any) => r.id !== rank.id
                    );
                    updateBuilderField("ranks", updated);
                    if (onSave) {
                      onSave();
                    }
                  }}
                  onSave={onSave}
                />
              );
            })}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                gap: "16px",
                marginTop: "16px",
              }}
            >
              <AddLinkButton
                type="button"
                onClick={() => {
                  const nextRankValue = getNextRank();
                  const newRank = {
                    id: Date.now().toString(),
                    rank: nextRankValue,
                    reward: "",
                    minimum: 1,
                    isEditing: true,
                    isNew: true,
                  };
                  updateBuilderField("ranks", [...sortedRanks, newRank]);
                }}
              >
                + Level
              </AddLinkButton>
            </div>
          </>
        )}
      </>
    );
  });
